import { createContext, useContext, useLayoutEffect, useRef, useState } from "react";
import { apiClient, type PublicOfficialIdentityMedia } from "@/shared/api/client";
import type { PetIdentityImage } from "@/shared/contracts";

export type GuestIdentitySubject = Readonly<{ petId: string; identity: PetIdentityImage | null | undefined }>;

/** Match the current public response. This is a local binding, not a fresh authorization decision. */
export function publicIdentityBinding(subject: GuestIdentitySubject | null, photoUrl: string | null | undefined, official: boolean): PublicOfficialIdentityMedia | null {
  const image = subject?.identity;
  if (!subject || !/^[A-Za-z0-9_-]+$/.test(subject.petId) || !official || !image
      || image.source_kind !== "platform_official" || image.official_state !== "usable"
      || !image.url || image.url !== photoUrl) return null;
  const prefix = apiClient.base + `/public/identity/${subject.petId}/official/`;
  if (!image.url.startsWith(prefix)) return null;
  const revisionText = image.url.slice(prefix.length);
  if (!/^[1-9][0-9]*$/.test(revisionText)) return null;
  const revision = Number(revisionText);
  if (!Number.isSafeInteger(revision) || image.version !== `o${revisionText}`) return null;
  return { petId: subject.petId, revision, url: image.url };
}

/**
 * 先别取图（UI-FIRST-IMPRESSION-116）：名单抽屉没打开过时为真，里面的访客头像先不发请求，打开后照常取。
 * 读屏照旧说「正在打开」，不说「打不开」；不改授权、不借别的图。
 */
export const GuestMediaDeferred = createContext(false);

type Lease = { key: string; active: boolean; controller: AbortController; blobUrl: string | null };
type Media = { lease: Lease; src: string | null; failed: boolean };

/** No shared query cache or member payload. Every committed owner gets a separate cancellable blob lifetime. */
/** width：缩略档（96、192、384），由调用方按显示边长和 /meta 能力定；null＝取原图。换档算换了一张，重新取（UI-REVIEW110-01）。 */
export function usePublicIdentityMedia(requested: PublicOfficialIdentityMedia | null, width: 96 | 192 | 384 | null = null) {
  const binding = useContext(GuestMediaDeferred) ? null : requested;
  const key = binding ? JSON.stringify([binding.petId, binding.revision, binding.url, width]) : "";
  const leaseRef = useRef<Lease | null>(null);
  const [media, setMedia] = useState<Media | null>(null);
  const request = apiClient.publicGuestOfficialIdentityGet;
  useLayoutEffect(() => {
    if (!binding) return;
    const lease: Lease = { key, active: true, controller: new AbortController(), blobUrl: null };
    leaseRef.current = lease;
    const current = () => lease.active && leaseRef.current === lease && !lease.controller.signal.aborted;
    const dispose = () => {
      lease.active = false;
      lease.controller.abort();
      if (lease.blobUrl) { URL.revokeObjectURL(lease.blobUrl); lease.blobUrl = null; }
      if (leaseRef.current === lease) leaseRef.current = null;
    };
    setMedia({ lease, src: null, failed: false });
    if (!request || !URL.createObjectURL || !URL.revokeObjectURL) {
      setMedia({ lease, src: null, failed: true });
      return dispose;
    }
    void request(width ? { ...binding, width } : binding, { signal: lease.controller.signal }).then(blob => {
      if (!current()) return;
      const url = URL.createObjectURL(blob);
      if (!current()) { URL.revokeObjectURL(url); return; }
      lease.blobUrl = url;
      setMedia({ lease, src: url, failed: false });
    }).catch(() => {
      if (current()) setMedia({ lease, src: null, failed: true });
    });
    return dispose;
    // A full immutable key is the committed identity; changes revoke before paint.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, request]);
  const owned = media && media.lease.key === key && media.lease.active && leaseRef.current === media.lease;
  const fail = () => {
    if (!owned || !media.lease.active || leaseRef.current !== media.lease) return;
    if (media.lease.blobUrl) { URL.revokeObjectURL(media.lease.blobUrl); media.lease.blobUrl = null; }
    setMedia({ lease: media.lease, src: null, failed: true });
  };
  return { src: owned ? media.src : null, failed: Boolean(owned && media.failed), fail };
}

